import { hide, show } from '@fnv-core/dom';
import { Logger } from '@fnv-core/logging';
import { IPlugin } from '@fnv-static/plugins';

const PLUGIN_SELECTOR = '.js-show-more';

interface IShowMoreOptions {
  target: string;
  stepSize: number;
  initialSize: number;
}

const HIDE_TRANSITION_IN_MILLISECONDS = 500;
const SHOW_TRANSITION_IN_MILLISECONDS = 800;

class ShowMore implements IPlugin {

  private _options: IShowMoreOptions;

  // Keep track of how many items are visible
  private _visibleCount: number;

  constructor(private _element: HTMLElement) {
    this._options = this._getOptions();
  }

  init() {
    const items = Array.from(document.querySelectorAll<HTMLElement>(this._options.target));
    if (!items || !items.length) {
      Logger.error('ShowMore plugin: Cannot initialise plugin; No elements to show.');
      return;
    }

    this._visibleCount = this._options.initialSize;

    if (items.length > this._options.initialSize) {
      // Hide all items except the initial size
      items.slice(this._options.initialSize).forEach(item => hide(item, 0));
    } else {
      hide(this._element);
    }

    this._element.addEventListener('click', (event) => {
      event.preventDefault();
      this._showMore();
    });
  }

  private _getOptions() {
    const options: IShowMoreOptions = {
      target: this._element.getAttribute('data-show-more-target'),
      stepSize: this._element.hasAttribute('data-show-more-step-size') ? parseInt(this._element.getAttribute('data-show-more-step-size')) : 3,
      initialSize: this._element.hasAttribute('data-show-more-initial-size') ? parseInt(this._element.getAttribute('data-show-more-initial-size')) : 5,
    };

    return options;
  }

  private _showMore() {
    const items = Array.from(document.querySelectorAll<HTMLElement>(this._options.target));
    if (items.length > this._visibleCount) {
      const untilIndex = this._visibleCount + this._options.stepSize;
      const itemsToShow = items.slice(this._visibleCount, untilIndex);
      itemsToShow.forEach(element => {
        show(element, SHOW_TRANSITION_IN_MILLISECONDS);
      });
      this._visibleCount += itemsToShow.length;
    } 

    if (items.length <= this._visibleCount) {
      hide(this._element, HIDE_TRANSITION_IN_MILLISECONDS);
    }
  }
}

export function initShowMore() {
  const pluginElements = document.querySelectorAll<HTMLElement>(PLUGIN_SELECTOR);
  Logger.info(`Initialising plugin ShowMore on ${pluginElements.length} elements.`);
  pluginElements.forEach(element => new ShowMore(element).init());
}
